
import { Button } from "@/components/ui/button";
import { useGameToast } from "@/hooks/useGameToast";
import type { Word, LogicRule } from "@/lib/game-utils";
import { getTodayString } from "@/utils/dateUtils";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { saveUserSubmission } from "@/services/user/userSubmissionService";

interface GameControlsProps {
  selectedWords: Array<{ word: Word; order: number }>;
  selectedRule: LogicRule | null;
  words: Word[];
  onSubmit: () => void;
  isReplay?: boolean;
}

export function GameControls({
  selectedWords,
  selectedRule,
  words,
  onSubmit,
  isReplay = false
}: GameControlsProps) {
  const { showWrongAnswerToast } = useGameToast();
  const [isSaving, setIsSaving] = useState(false);
  
  // Only disable the button if we're missing selections or in replay mode
  // or if we're currently saving
  const isSubmitDisabled = selectedWords.length !== words.length || isReplay || isSaving;

  const handleSubmit = async () => {
    // Check if all words are selected
    if (selectedWords.length !== words.length) {
      showWrongAnswerToast("You need to select all words");
      return;
    }
    
    console.log("Lock In button clicked, submitting game");
    setIsSaving(true);
    
    try {
      // Get game ID for today
      const today = getTodayString();
      const userEmail = localStorage.getItem("user_email");
      
      if (userEmail) {
        // Try to get the game ID for today
        const { data: gameData, error: gameError } = await supabase
          .from('games')
          .select('id')
          .eq('date', today)
          .maybeSingle();
            
        if (!gameError && gameData?.id) {
          // Create the ranking array from selected words
          const ranking = [...selectedWords]
            .sort((a, b) => a.order - b.order)
            .map(sw => {
              if (typeof sw.word === 'string') return sw.word;
              if (typeof sw.word === 'object' && sw.word !== null) {
                if ('word' in sw.word) return String((sw.word as any).word);
                return JSON.stringify(sw.word);
              }
              return String(sw.word);
            });
          
          // Save silently in the background  
          saveUserSubmission(
            gameData.id,
            userEmail,
            ranking
          );
        }
      }
    } catch (error) {
      console.error("[GameControls] Error in background save:", error);
    } finally {
      setIsSaving(false);
      // Call the onSubmit handler to continue game flow
      onSubmit();
    }
  };

  // Don't render anything in replay mode
  if (isReplay) {
    return null;
  }

  return (
    <Button
      onClick={handleSubmit}
      disabled={isSubmitDisabled}
      className={`w-auto min-w-[150px] max-w-[200px] py-6 px-4 text-lg font-medium rounded-full ${
        isSubmitDisabled 
          ? "bg-gray-400 text-gray-100" 
          : "bg-black text-white hover:bg-gray-800 active:bg-gray-700"
      }`}
    >
      {isSaving ? "Saving..." : "Lock In"}
    </Button>
  );
}
